import { useRef } from 'react';
import { gql } from '@apollo/client';
import { FontIcon } from '@fluentui/react';
import {
  PagedDataGrid,
  type IPagedDataGridRefType,
  type DataGridColumn,
  type ICommandBarItem,
} from '@moko/ui';
import { client } from '../../services/apolloClient';
import { formatDate } from '@moko/utils';

interface IActivityLog {
  id: number;
  loggedAt: string;
  username: string;
  action: string;
  summary: string;
}

const GET_ACTIVITY_LOGS = gql`
  query GetActivityLogs($limit: Int!, $offset: Int!, $search: String) {
    activityLogs(limit: $limit, offset: $offset, search: $search) {
      nodes {
        id
        loggedAt
        username
        displayName
        action
        summary
      }
      pageInfo {
        hasNextPage
      }
      totalCount
    }
  }
`;

export const ActivityLogList = () => {
  const pagedDataGridRef = useRef<IPagedDataGridRefType>(null);

  const getAction = async (
    _abortController: AbortController,
    pageSize: number,
    pageNo: number,
    _orderBy?: string,
    search?: string
  ) => {
    // Note: AbortSignal not passed to Apollo due to known bug with React StrictMode
    // See: https://github.com/apollographql/apollo-client/issues/9632
    const { data } = await client.query({
      query: GET_ACTIVITY_LOGS,
      variables: {
        limit: pageSize,
        offset: (pageNo - 1) * pageSize,
        search: search || '',
      },
      fetchPolicy: 'network-only',
    });

    return {
      isLastPage: !data.activityLogs.pageInfo.hasNextPage,
      items: data.activityLogs.nodes,
      pageCount: Math.ceil(data.activityLogs.totalCount / pageSize),
      pageNo,
      pageSize,
      rowsCount: data.activityLogs.totalCount,
    };
  };

  const columns: DataGridColumn<IActivityLog>[] = [
    {
      key: 'loggedAt',
      name: 'Time',
      minWidth: 150,
      renderCell: (item) => formatDate(item.loggedAt, true),
    },
    { key: 'displayName', name: 'User', minWidth: 100 },
    { key: 'action', name: 'Action', minWidth: 100 },
    { key: 'summary', name: 'Summary', minWidth: 200 },
  ];

  const commandBarItems: ICommandBarItem[] = [
    {
      key: 'refresh',
      text: 'Refresh',
      icon: <FontIcon iconName="Refresh" />,
      onClick: (ctx) => ctx.refresh(),
    },
  ];

  return (
    <PagedDataGrid<IActivityLog>
      ref={pagedDataGridRef}
      columns={columns}
      getAction={getAction}
      commandBarItems={commandBarItems}
      pageSize={10}
    />
  );
};
